import { useState, useEffect, useLayoutEffect, useCallback, useRef } from 'react';
import {
  getStoredSnapToGrid,
  saveSnapToGrid,
  getStoredGanttSwimlane,
  saveGanttSwimlane,
} from './utils/toolbarStorage';

const resolveInitialViewType = (initialView) => {
  if (typeof initialView === 'string') return initialView;
  if (initialView && typeof initialView === 'object' && typeof initialView.type === 'string') {
    return initialView.type;
  }
  return null;
};

/**
 * useToolbarState Hook
 * 
 * Manages toolbar state including view mode, snap, auto-arrange, and focus mode.
 * Persists snapToGrid and ganttSwimlane settings per campaign in localStorage.
 * 
 * @param {Object} flowchartRef - Ref to the flowchart component
 * @param {string} campaignId - Current campaign ID for per-campaign settings
 * @param {string} initialView - Initial view mode (flow, kanban, table, etc.)
 */
export const useToolbarState = (flowchartRef, campaignId = '', initialView = null) => {
  // Initialize with initialView if provided, otherwise default to 'flow'
  const [currentView, setCurrentView] = useState(() => resolveInitialViewType(initialView) || 'flow');
  const [autoArrange, setAutoArrange] = useState(false);
  const [isFocusMode, setIsFocusMode] = useState(false);
  const [focusedTaskIds, setFocusedTaskIds] = useState(null); // Track focused task IDs for persistence
  const [hasSelection, setHasSelection] = useState(false);
  
  // Per-campaign persisted settings
  const [snapToGrid, setSnapToGrid] = useState(() => getStoredSnapToGrid(campaignId));
  const [ganttSwimlane, setGanttSwimlane] = useState(() => getStoredGanttSwimlane(campaignId));
  
  // Track previous initial view type + campaign so we restore even when two
  // tabs share the same viewMode string (object identity used to hide that).
  const prevInitialViewTypeRef = useRef(resolveInitialViewType(initialView));
  const prevCampaignIdRef = useRef(campaignId);

  // Reload settings when campaign changes
  useEffect(() => {
    setSnapToGrid(getStoredSnapToGrid(campaignId));
    setGanttSwimlane(getStoredGanttSwimlane(campaignId));
  }, [campaignId]);

  // Update view when initialView or campaign changes (URL, history, tab switch).
  // useLayoutEffect so the first paint after navigation matches the intended view — useEffect
  // runs after paint and caused a visible flash (e.g. flow) before switching to table.
  useLayoutEffect(() => {
    const viewType = resolveInitialViewType(initialView);
    const campaignChanged = campaignId !== prevCampaignIdRef.current;
    const viewChanged = viewType && viewType !== prevInitialViewTypeRef.current;
    if (viewType && (campaignChanged || viewChanged)) {
      setCurrentView(viewType);
    }
    prevCampaignIdRef.current = campaignId;
    prevInitialViewTypeRef.current = viewType;
  }, [initialView, campaignId]);

  // Handle snap toggle with persistence
  const handleSnapToggle = useCallback(() => {
    flowchartRef.current?.toggleSnapToGrid();
    setSnapToGrid(prev => {
      const next = !prev;
      saveSnapToGrid(campaignId, next);
      return next;
    });
  }, [flowchartRef, campaignId]);

  // Handle Gantt swimlane change with persistence
  const handleGanttSwimlaneChange = useCallback((newSwimlane) => {
    setGanttSwimlane(newSwimlane);
    saveGanttSwimlane(campaignId, newSwimlane);
  }, [campaignId]);
  
  // Handle auto-arrange toggle
  const handleAutoArrangeToggle = useCallback(() => {
    setAutoArrange(prev => {
      const newValue = !prev;
      if (newValue) {
        setTimeout(() => flowchartRef.current?.autoLayout(), 0);
      }
      return newValue;
    });
  }, [flowchartRef]);
  
  // Handle exit focus mode
  const handleExitFocusMode = useCallback(() => {
    flowchartRef.current?.exitFocusMode();
    setIsFocusMode(false);
    setFocusedTaskIds(null);
  }, [flowchartRef]);
  
  // Focus selected nodes, or explicit task IDs (e.g. Fokus modal when nothing was selected)
  const handleFocusSelected = useCallback((overrideTaskIds) => {
    if (overrideTaskIds && overrideTaskIds.size > 0) {
      flowchartRef.current?.focusTasks(Array.from(overrideTaskIds), false);
    } else {
      flowchartRef.current?.focusSelectedTasks();
    }
  }, [flowchartRef]);
  
  // Handle focus mode change from FlowchartView
  // Also receives the focused task IDs for persistence across tab switches
  const handleFocusModeChange = useCallback((isActive, taskId, isTransition, allFocusedIds) => {
    setIsFocusMode(isActive);
    if (!isActive) {
      setFocusedTaskIds(null);
    } else if (allFocusedIds) {
      // Store the focused task IDs for persistence
      setFocusedTaskIds(allFocusedIds);
    }
  }, []);
  
  // PERFORMANCE FIX: Instead of polling every 200ms (which was blocking the main thread),
  // we now listen for selection changes via a custom event dispatched by FlowchartView
  useEffect(() => {
    const handleSelectionChange = (e) => {
      setHasSelection(e.detail?.hasSelection || false);
    };
    
    window.addEventListener('flowchartSelectionChange', handleSelectionChange);
    return () => window.removeEventListener('flowchartSelectionChange', handleSelectionChange);
  }, []);

  return {
    currentView,
    setCurrentView,
    snapToGrid,
    autoArrange,
    isFocusMode,
    setIsFocusMode, // For instant FOKUS restoration
    focusedTaskIds, // Expose for passing to FlowchartView as initial state
    setFocusedTaskIds, // For instant FOKUS restoration
    hasSelection,
    ganttSwimlane,
    setGanttSwimlane: handleGanttSwimlaneChange, // Saves to global storage
    setGanttSwimlaneRaw: setGanttSwimlane, // Raw setter without global save (for custom view restoration)
    handleSnapToggle,
    handleAutoArrangeToggle,
    handleExitFocusMode,
    handleFocusSelected,
    handleFocusModeChange,
  };
};

export default useToolbarState;

